import React from 'react';
import styled from '@emotion/styled';
import Button from '@via-profit/ui-kit/Button';
import { useTheme } from '@via-profit/ui-kit/ThemeProvider';
import Autocomplete, { AutocompleteItem } from '@via-profit/ui-kit/Autocomplete';
import Highlighted from '@via-profit/ui-kit/Highlighted';

import { fetchAutocomplete, reportNoMatch, reportNotAddress, type SearchArea } from '../../api/geocoder';
import type { GeocodeResult } from '../../api/types';
import { splitDisplay } from '../../api/addressDisplay';
import { SearchIcon, getResultIcon } from './icons';

/** Значение, подставленное в поле снаружи (например, точка с карты). */
export interface SearchBoxPreset {
  readonly result: GeocodeResult;
  /** Текст в поле ввода. */
  readonly label: string;
}

export interface SearchBoxProps {
  /**
   * logId - id ответа /autocomplete в search_log (null, если запрос не
   * успел долететь до бэкенда с логированием). Нужен вызывающей стороне,
   * чтобы потом подтвердить выбор через отдельную кнопку, а не слать
   * feedback на каждый клик по автокомплиту.
   */
  readonly onSelect: (result: GeocodeResult, logId: string | null) => void;
  /** Текущий центр карты - используется бэкендом, чтобы при неоднозначном
   *  названии (улица/город, встречающиеся во многих местах) в первую очередь
   *  отдавать варианты, ближайшие к тому, что сейчас видно на карте. */
  readonly near?: SearchArea | null;
  readonly placeholder?: string;
  /** Кнопки "ничего не подходит" / "это не адрес" - нужны в обычном поиске,
   *  в полях маршрута только мешают. */
  readonly showFeedback?: boolean;
  /** Поле очищено - крестиком или стиранием текста. */
  readonly onClear?: () => void;
  /** Новый объект - поле показывает его label; onSelect при этом не
   *  вызывается, вызывающая сторона и так знает, что подставила. */
  readonly preset?: SearchBoxPreset | null;
  /** Иконка слева в поле; по умолчанию - лупа. */
  readonly icon?: React.ReactElement;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const StyledAutocompleteItem = styled(AutocompleteItem)`
  align-items: flex-start;
`;

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
`;

const ItemPrimary = styled.span`
  font-weight: 600;
  color: inherit;
  overflow-wrap: break-word;
`;

const ItemSecondary = styled.span`
  font-size: 0.82em;
  color: inherit;
  opacity: 0.6;
  overflow-wrap: break-word;
`;

const NoMatchRow = styled.div`
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  gap: 8px;
  font-size: 12px;
`;

// кнопки поверх карты - на непрозрачном фоне, иначе их не прочитать
const FeedbackButton = styled(Button)`
  background-color: ${({ theme }) => theme.color.surface.toString()};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => (theme.isDark ? theme.color.surface.lighten(8) : theme.color.surface.darken(6)).toString()};
  }
`;

const SearchBox: React.FC<SearchBoxProps> = ({
  onSelect,
  near,
  placeholder = 'Найти адрес…',
  showFeedback = true,
  onClear,
  preset,
  icon,
}) => {
  const theme = useTheme();
  const [value, setValue] = React.useState<GeocodeResult | null>(null);
  const [items, setItems] = React.useState<GeocodeResult[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  // текущий текст, введённый пользователем - используется вместо fullAddress
  // выбранного результата, чтобы выбор из автокомплита не менял строку ввода
  const queryRef = React.useRef('');
  // всегда самый свежий центр карты - читается из debounce-таймера, поэтому
  // не может быть просто замыканием над пропом на момент вызова runSearch
  const nearRef = React.useRef(near);
  nearRef.current = near;
  // id последнего /autocomplete-ответа в search_log - нужен, чтобы сообщить
  // бэкенду, какой из этих результатов пользователь в итоге выбрал (или что
  // ни один не подошёл)
  const [logId, setLogId] = React.useState<string | null>(null);
  const [noMatchReported, setNoMatchReported] = React.useState(false);
  const [notAddressReported, setNotAddressReported] = React.useState(false);

  React.useEffect(() => {
    if (!preset) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    queryRef.current = preset.label;
    setValue(preset.result);
    setItems([]);
    setIsOpen(false);
    setIsLoading(false);
    setLogId(null);
  }, [preset]);

  const runSearch = React.useCallback((query: string) => {
    abortRef.current?.abort();
    setNoMatchReported(false);
    setNotAddressReported(false);

    if (query.trim().length < MIN_QUERY_LENGTH) {
      setItems([]);
      setIsOpen(false);
      setIsLoading(false);
      setLogId(null);

      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);

    fetchAutocomplete(query, 5, controller.signal, nearRef.current)
      .then(({ results, logId: newLogId }) => {
        setItems(results);
        setIsOpen(results.length > 0);
        setLogId(newLogId);
      })
      .catch((error: unknown) => {
        if ((error as Error).name !== 'AbortError') {
          console.error('Autocomplete failed:', error);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleNoMatch = React.useCallback(() => {
    if (!logId) return;
    reportNoMatch(logId);
    setNoMatchReported(true);
  }, [logId]);

  const handleNotAddress = React.useCallback(() => {
    if (!logId) return;
    reportNotAddress(logId);
    setNotAddressReported(true);
  }, [logId]);

  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    },
    [],
  );

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
    event => {
      const query = event.currentTarget.value;
      queryRef.current = query;
      if (query.trim() === '') onClear?.();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    },
    [runSearch, onClear],
  );

  return (
    <div>
      <Autocomplete<GeocodeResult>
        value={value}
        items={items}
        isLoading={isLoading}
        isOpen={isOpen}
        placeholder={placeholder}
        fullWidth
        startIcon={icon ?? <SearchIcon />}
        name="q"
        autoComplete="off"
        onRequestOpen={() => items.length > 0 && setIsOpen(true)}
        onRequestClose={() => setIsOpen(false)}
        onInputChange={onInputChange}
        onChange={item => {
          setValue(item);
          if (item) {
            onSelect(item, logId);
            setLogId(null);
            setNoMatchReported(false);
            setNotAddressReported(false);
          } else {
            onClear?.();
          }
        }}
        selectedItemToString={() => queryRef.current}
        filterItems={list => list}
      >
        {({ item, inputValue }, itemProps) => {
          const { primary, secondary } = splitDisplay(item);

          return (
            <StyledAutocompleteItem {...itemProps} key={item.id} startIcon={getResultIcon(item)}>
              <ItemBody>
                <ItemPrimary>
                  <Highlighted text={primary} highlight={inputValue} />
                </ItemPrimary>
                {secondary && (
                  <ItemSecondary>
                    <Highlighted text={secondary} highlight={inputValue} />
                  </ItemSecondary>
                )}
              </ItemBody>
            </StyledAutocompleteItem>
          );
        }}
      </Autocomplete>

      {showFeedback && logId && (
        <NoMatchRow>
          <FeedbackButton
            type="button"
            variant="outlined"
            color={noMatchReported ? theme.color.success.toString() : theme.color.error.toString()}
            onClick={handleNoMatch}
            disabled={noMatchReported}
          >
            {noMatchReported
              ? 'Записано: верного адреса нет в выдаче ✓'
              : items.length > 0
                ? 'Ничего из списка не подходит'
                : 'Такого адреса нет в базе — записать'}
          </FeedbackButton>
          <FeedbackButton
            type="button"
            variant="outlined"
            color={notAddressReported ? theme.color.success.toString() : 'default'}
            onClick={handleNotAddress}
            disabled={notAddressReported}
          >
            {notAddressReported ? 'Записано: не адрес ✓' : 'Это не адрес'}
          </FeedbackButton>
        </NoMatchRow>
      )}
    </div>
  );
};

export default SearchBox;
